import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useSideEffect } from '../../data/store/Effects/side-effects'
import { creditApplicationEffects, creditApplicationSelectors } from '../../data/store/CreditApplicationStore'
import { Enum } from '../../data/types/constants'
import { reportErrorToServer } from '../../error-logger'
import { useAppSelector } from '../../data/store'

function FeedBackPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const creditApplication = useAppSelector(creditApplicationSelectors.getFilteredCreditApplication)
  const justGaveFeedback = useAppSelector(creditApplicationSelectors.getJustGaveFeedback)
  const dispatchEffect = useSideEffect()
  const [executionStatus, setExecutionStatus] = React.useState(Enum.EExecutionStatus.NotStarted)
  const currentLanguage = t<string>('common.language')

  const isAwaitingContractSignature = creditApplication?.requiredDocuments.some(
    (x) => x.typeId === Enum.EDocumentType.SignedCVT && x.status === Enum.EDocumentStatus.AwaitingDocument,
  )

  const bankAccountAnalyzingStatements =
    !creditApplication?.allIncomesConfirmed &&
    creditApplication?.requiredExternalSteps &&
    creditApplication?.requiredExternalSteps?.length > 0 &&
    creditApplication?.requiredExternalSteps.every(
      (x) => x.status === Enum.ERequiredExternalStepStatus.Completed && x.externalStepId === 'bankAccount',
    )

  const getMessage = () => {
    if (bankAccountAnalyzingStatements) return <h3>{t<string>('bankAccount.analyzingStatements')}</h3>

    if (creditApplication?.loanCreated) return <h3>{t<string>('common.loanCreated')}</h3>

    if (isAwaitingContractSignature) return <h3>{t<string>('common.awaitingContractSignature')}</h3>

    if (
      creditApplication?.requiredDocuments.every(
        (x) => x.status === Enum.EDocumentStatus.Approved || x.status === Enum.EDocumentStatus.AwaitingApproval,
      )
    )
      return (
        <div>
          <h3>{t<string>('common.awaitingLoanCreation')}</h3>
          <div className="item">
            <button
              type="button"
              className="btn"
              onClick={() => navigate(`/creditApplication/${id as string}/coapplicant`)}
              disabled={creditApplication?.hasCoapplicant}
            >
              <span>{t<string>('common.addCoapplicant')}</span>
              <i className="fa-regular fa-user-plus" />
            </button>
          </div>
        </div>
      )
    return <h1>{t<string>('common.hasBeenSubmitted')}</h1>
  }

  const getStateMessage = () => {
    if (isAwaitingContractSignature) return t<string>('common.almostDone')
    if (creditApplication?.loanCreated) return t<string>('common.goodNews')
    return t<string>('common.AnalysisInProgress')
  }

  const getLabel = () => {
    if (executionStatus === Enum.EExecutionStatus.InProgress) return t<string>('common.sendingEmail')
    if (executionStatus === Enum.EExecutionStatus.Completed) return t<string>('common.emailSent')
    if (executionStatus === Enum.EExecutionStatus.Error) return t<string>('common.emailError')
    if (isAwaitingContractSignature) return t<string>('common.resendEmail')
    return t<string>('common.questions')
  }

  const getIcon = () => {
    if (executionStatus === Enum.EExecutionStatus.InProgress) return <div className="mini-spinner" />
    if (executionStatus === Enum.EExecutionStatus.Completed) return <i className="fa-regular fa-check" />
    if (executionStatus === Enum.EExecutionStatus.Error) return <i className="fa-regular fa-exclamation-triangle" />
    if (executionStatus === Enum.EExecutionStatus.NotStarted) return <i className="fa-regular fa-paper-plane-top" />
    return ''
  }

  const handleClick = () => {
    if (isAwaitingContractSignature) {
      setExecutionStatus(Enum.EExecutionStatus.InProgress)
      dispatchEffect(creditApplicationEffects.resendElectronicSignature(id as string))
        .then(() => setExecutionStatus(Enum.EExecutionStatus.Completed))
        .catch((e) => {
          setExecutionStatus(Enum.EExecutionStatus.Error)
          reportErrorToServer(e)
        })
    } else if (creditApplication?.loanCreated) {
      const url = currentLanguage === 'fr' ? 'https://ifinancecanada.com/fr/' : 'https://ifinancecanada.com/'
      window.open(url, '_blank')
    } else dispatchEffect(creditApplicationEffects.emailResumeLink(id as string))
  }

  return (
    <main className="general-message">
      <h4 className="subtitle">{getStateMessage()}</h4>
      {justGaveFeedback && (
        <>
          <h3>{t<string>('feedback.thankYou')}</h3> <br />
        </>
      )}

      {getMessage()}
      {isAwaitingContractSignature && (
        <button
          disabled={
            executionStatus === Enum.EExecutionStatus.InProgress || executionStatus === Enum.EExecutionStatus.Completed
          }
          type="button"
          className="btn btn-blue"
          onClick={handleClick}
        >
          <span>{getLabel()}</span>
          {getIcon()}
        </button>
      )}
      {creditApplication?.loanCreated && (
        <button type="button" className="btn btn-blue" onClick={handleClick}>
          <span>{getLabel()}</span>
          <i className="fa-regular fa-check" />
        </button>
      )}
    </main>
  )
}

export default FeedBackPage
